import { useQuery } from '@tanstack/react-query';
import { Avatar, Icon, ListGroup, ListRow, SegmentedControl, Skeleton } from '@uplink/ui';
import { useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { CURRENCY_SIGNS, type Currency, LANG_NAMES, type Lang, usePrefs } from '../prefs.tsx';
import { CopyText } from '../servers-ui.tsx';
import { SshKeysSection } from '../ssh-keys.tsx';
import { haptic, openPayUrl, openSupport, type ThemePref } from '../telegram.ts';
import { AccountSection } from '../web/AccountSection.tsx';

export function ProfileScreen({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const prefs = usePrefs();
  const { t } = prefs;
  const me = useQuery(trpc.me.queryOptions());
  const config = useQuery(trpc.config.queryOptions());
  const invite = useQuery(trpc.referral.summary.queryOptions());
  const share = (link: string) =>
    openPayUrl(
      `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(t.profile.inviteShareText)}`,
    );

  return (
    <main className="ul-screen ul-screen--sections">
      {me.data ? (
        <header className="ul-profile">
          <Avatar src={me.data.photoUrl} name={me.data.firstName} size={72} />
          <span className="ul-profile__name">{me.data.firstName}</span>
          <span className="ul-profile__meta">
            {me.data.telegramId
              ? `${me.data.username ? `@${me.data.username} · ` : ''}ID ${me.data.telegramId}`
              : me.data.email}
          </span>
        </header>
      ) : (
        <Skeleton height={140} />
      )}

      <ListGroup
        header={t.profile.settings}
        footer={prefs.currency !== 'USD' && !prefs.ratesReady ? t.profile.noRates : t.profile.currencyFooter}
      >
        <div className="ul-setting">
          <span className="ul-setting__label">
            <Icon name="language" size={16} />
            {t.profile.language}
          </span>
          <SegmentedControl<Lang>
            label={t.profile.language}
            value={prefs.lang}
            onChange={(v) => {
              haptic.select();
              prefs.setLang(v);
            }}
            // Endonyms: each language is written in itself
            options={(Object.keys(LANG_NAMES) as Lang[]).map((l) => ({ value: l, label: LANG_NAMES[l] }))}
          />
        </div>
        <div className="ul-setting">
          <span className="ul-setting__label">
            <Icon name="currency" size={16} />
            {t.profile.currency}
          </span>
          <SegmentedControl<Currency>
            label={t.profile.currency}
            value={prefs.currency}
            onChange={(v) => {
              haptic.select();
              prefs.setCurrency(v);
            }}
            options={(Object.keys(CURRENCY_SIGNS) as Currency[]).map((c) => ({
              value: c,
              label: `${CURRENCY_SIGNS[c]} ${c}`,
            }))}
          />
        </div>
        <div className="ul-setting">
          <span className="ul-setting__label">
            <Icon name={prefs.theme === 'dark' ? 'moon' : 'sun'} size={16} />
            {t.profile.theme}
          </span>
          <SegmentedControl<ThemePref>
            label={t.profile.theme}
            value={prefs.theme}
            onChange={(v) => {
              haptic.select();
              prefs.setTheme(v);
            }}
            options={[
              { value: 'auto', label: t.profile.themeAuto },
              { value: 'light', label: t.profile.themeLight },
              { value: 'dark', label: t.profile.themeDark },
            ]}
          />
        </div>
      </ListGroup>

      <AccountSection />

      {invite.data?.link ? (
        <ListGroup
          header={t.profile.invite}
          footer={invite.data.percent > 0 ? t.profile.inviteText : undefined}
        >
          <ListRow
            icon="gift"
            title={
              invite.data.percent > 0
                ? t.profile.inviteTitle(invite.data.percent)
                : t.profile.inviteTitleNoReward
            }
            subtitle={invite.data.link.replace(/^https:\/\//, '')}
            trailing={<CopyText text={invite.data.link} label="" />}
          />
          <ListRow icon="users" title={t.profile.invited} value={invite.data.invited} />
          <ListRow icon="wallet" title={t.profile.earned} value={prefs.usd(invite.data.earnedCents)} />
          <ListRow
            icon="arrowUp"
            title={t.profile.inviteShare}
            chevron
            onClick={() => share(invite.data!.link!)}
          />
        </ListGroup>
      ) : null}

      <ListGroup>
        <ListRow icon="gift" title={t.profile.giftRow} chevron onClick={() => nav.push({ name: 'gift' })} />
      </ListGroup>

      <SshKeysSection />

      {me.data?.isAdmin ? (
        <ListGroup>
          <ListRow icon="chart" title="Админ-панель" chevron onClick={() => nav.push({ name: 'admin' })} />
        </ListGroup>
      ) : null}

      <ListGroup header={t.profile.support} footer={t.profile.supportFooter}>
        <ListRow
          icon="support"
          title={t.profile.supportRow}
          chevron
          onClick={() => openSupport(config.data?.supportUrl)}
        />
      </ListGroup>
    </main>
  );
}
